import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import AccountShell from "@/components/vangard/AccountShell";

const TIERS = [
  { id: "silver", name: "Silver", price: "vc.mem.free", period: "", tag: "vc.mem.silver-tag", body: "vc.tiers.silver-desc", features: ["vc.mem.f.silver-1", "vc.mem.f.silver-2", "vc.mem.f.silver-3", "vc.mem.f.silver-4", "vc.mem.f.silver-5"] },
  { id: "gold", name: "Gold", price: "$3,000", period: "vc.mem.per-year", tag: "vc.mem.gold-tag", body: "vc.tiers.gold-desc", features: ["vc.mem.f.gold-1", "vc.mem.f.gold-2", "vc.mem.f.gold-3", "vc.mem.f.gold-4", "vc.mem.f.gold-5", "vc.mem.f.gold-6"] },
  { id: "vip", name: "VIP", price: "$7,000", period: "vc.mem.per-year", tag: "vc.mem.vip-tag", body: "vc.tiers.vip-desc", features: ["vc.mem.f.vip-1", "vc.mem.f.vip-2", "vc.mem.f.vip-3", "vc.mem.f.vip-4", "vc.mem.f.vip-5", "vc.mem.f.vip-6"] },
  { id: "corporate", name: "Corporate", price: "vc.mem.custom", period: "", tag: "vc.mem.corp-tag", body: "vc.tiers.corp-desc", features: ["vc.mem.f.corp-1", "vc.mem.f.corp-2", "vc.mem.f.corp-3", "vc.mem.f.corp-4", "vc.mem.f.corp-5", "vc.mem.f.corp-6"] },
];

export default function AccountMembershipPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const current = (user?.membership_tier || "silver").toLowerCase();

  return (
    <AccountShell active="/account/membership" title={t("vc.acct.nav.plans")}>
      <div data-testid="account-membership-page">
        <p className="text-sm text-neutral-400 font-light max-w-lg">{t("vc.tiers.sub")}</p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {TIERS.map((tier) => {
            const isCurrent = current === tier.id;
            return (
              <div
                key={tier.id}
                data-testid={`plan-card-${tier.id}`}
                className={`relative rounded-2xl p-6 flex flex-col border bg-[#0A0A0A]/80 ${
                  isCurrent ? "border-white/45 shadow-[0_0_40px_rgba(255,255,255,0.05)]" : "border-white/[0.08]"
                }`}
              >
                {isCurrent && (
                  <span className="absolute -top-2.5 left-6 bg-white text-black px-3 py-0.5 rounded-full text-[9px] tracking-[0.25em] uppercase font-medium">
                    {t("vc.mem.current")}
                  </span>
                )}
                <div className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t(tier.tag)}</div>
                <h2 className="font-serif text-2xl mt-2.5">{tier.name}</h2>
                <div className="mt-1.5 flex items-baseline gap-1.5">
                  <span className="text-lg">{tier.price.startsWith("vc.") ? t(tier.price) : tier.price}</span>
                  {tier.period && <span className="text-[11px] text-neutral-500">{t(tier.period)}</span>}
                </div>
                <p className="mt-3 text-[13px] text-neutral-400 font-light leading-relaxed">{t(tier.body)}</p>

                <div className="my-4 h-px bg-white/[0.08]" />

                <ul className="space-y-2 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[12px] text-neutral-300 font-light">
                      <Check size={12} className="mt-1 flex-shrink-0 text-white/50" />
                      {t(f)}
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <div className="mt-5 text-center px-4 py-2.5 rounded-full bg-white/[0.05] text-neutral-500 text-[10px] tracking-[0.25em] uppercase">
                    {t("vc.mem.current-plan")}
                  </div>
                ) : (
                  <Link
                    to="/account/concierge"
                    data-testid={`plan-request-${tier.id}`}
                    className="mt-5 text-center px-4 py-2.5 rounded-full border border-white/20 text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                  >
                    {tier.id === "corporate" ? t("vc.tiers.contact") : t("vc.acct.plans.request")}
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 rounded-2xl border border-white/[0.08] bg-[#0A0A0A]/80 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="max-w-xl">
            <div className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.mem.bespoke-eyebrow")}</div>
            <div className="text-base mt-1.5">{t("vc.mem.bespoke-title-1")} {t("vc.mem.bespoke-title-2")}</div>
            <p className="text-[13px] text-neutral-400 font-light mt-1.5 leading-relaxed">{t("vc.mem.bespoke-body")}</p>
          </div>
          <Link
            to="/account/concierge"
            className="shrink-0 inline-flex items-center justify-center bg-white text-black px-5 py-2.5 rounded-full text-[10px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
            data-testid="plans-contact-concierge"
          >
            {t("vc.tiers.contact")}
          </Link>
        </div>
      </div>
    </AccountShell>
  );
}
